import * as React from 'react';
import classnames from 'classnames';
import { debounce } from 'lodash';

import PageSection from 'components/page/page-section/PageSection';
import InfluencerSelector from 'components/influencer-selector/InfluencerSelector';
import InfluencerSelectorSearch from 'components/influencer-selector/InfluencerSelectorSearch';
import Spinner from 'components/spinner/Spinner';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { INFLUENCER_SELECTOR_AUTO_SELECT_DELAY, doesMatchSearchQuery } from 'utils/influencers';

import type {
  Data,
  MixedSelectDropdownItem,
  SelectOption,
  Subcampaign
} from 'types';

type Props = {
  title?: string,
  filterOptions?: Array<SelectOption>,
  isLoading: boolean,
  disabled: boolean,
  defaultCurrentFilter?: string | null,
  defaultSelectedItemId: number | undefined | null,
  items: Array<Subcampaign>,
  resetButton?: {
    label: string,
    icon: string
  },
  shouldFilterOut?: (item: Subcampaign, deliverableTypeFilter: string) => boolean,
  onItemSelected: (item?: Subcampaign | null) => void,
  onSelectedByFilter?: (item?: Subcampaign | null) => void,
  onFilterChanged?: (deliverableTypeFilter: string) => void,
  onSortItems: (items: Array<Subcampaign>) => Array<Subcampaign>
};

type State = {
  sortedItems: Array<Subcampaign>,
  selectedItem: Subcampaign | undefined | null,
  currentFilter: 'all' | (string & {}),
  searchQuery: string
};

class CampaignInfluencerSelector extends React.PureComponent<Props, State> {
  state = {
    sortedItems: this.props.onSortItems(this.props.items),
    selectedItem: this.props.items.find(s => s.id === this.props.defaultSelectedItemId),
    currentFilter: this.props.defaultCurrentFilter || 'all',
    searchQuery: ''
  };

  componentDidUpdate(prevProps: Props) {
    const { items, onItemSelected, resetButton } = this.props;
    const { selectedItem, sortedItems } = this.state;

    if (items !== prevProps.items) {
      this.sortItems();
      return;
    }

    if (!selectedItem && sortedItems.length > 0 && !resetButton) {
      onItemSelected(sortedItems[0]);
    }
  }

  sortItems() {
    const { items, onSortItems } = this.props;

    this.setState({
      sortedItems: onSortItems(items),
      selectedItem: items.find(s => s.id === this.props.defaultSelectedItemId)
    });
  }

  getSelectedItem(influencerProfileId?: number | null): Subcampaign | undefined {
    const { items } = this.props;

    return items.find(s => s.creatorProfile.id === influencerProfileId);
  }

  getDropdownItems = (): Array<MixedSelectDropdownItem> => {
    const { filterOptions } = this.props;
    const { currentFilter } = this.state;

    return (filterOptions || []).map(item => ({
      identifier: item.value,
      isDisabled: false,
      isSelected: currentFilter === item.value,
      label: item.label,
      value: item.value
    }));
  };

  getFilteredItems(): Array<Subcampaign> {
    const { shouldFilterOut } = this.props;
    const { sortedItems, currentFilter, searchQuery } = this.state;
    let filteredItems = sortedItems;

    if (currentFilter !== 'all' || searchQuery) {
      filteredItems = filteredItems.filter(item => {
        if (shouldFilterOut && shouldFilterOut(item, currentFilter)) {
          return false;
        }

        if (searchQuery && !doesMatchSearchQuery(searchQuery, item.creatorProfile)) {
          return false;
        }

        return true;
      });
    }

    return filteredItems;
  }

  handleInfluencerSelected = (influencerProfileId?: number | null) => {
    const { onItemSelected } = this.props;
    const nextSelectedItem = this.getSelectedItem(influencerProfileId);

    this.setState({
      selectedItem: nextSelectedItem
    });

    onItemSelected(nextSelectedItem);
  };

  handleInfluencersSearch = (searchQuery: string) => {
    this.setState({
      searchQuery
    } as any, debounce(this.handleAutoSelectInfluencer, INFLUENCER_SELECTOR_AUTO_SELECT_DELAY));
  };

  handleTasksChanged = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    const { onFilterChanged } = this.props;
    const value = selectedItem?.identifier || updatedItems[0].identifier;

    this.setState({
      currentFilter: value
    }, debounce(this.handleAutoSelectInfluencer, 0));

    if (onFilterChanged) {
      onFilterChanged(selectedItem?.identifier || updatedItems[0].identifier);
    }
  };

  handleAutoSelectInfluencer = (): void => {
    const { onSelectedByFilter } = this.props;

    if (onSelectedByFilter) {
      const filteredItems = this.getFilteredItems();

      onSelectedByFilter(filteredItems[0]);

      this.setState({
        selectedItem: filteredItems[0]
      });
    }
  };

  renderContent(): React.ReactNode {
    const { filterOptions, resetButton } = this.props;
    const { selectedItem, currentFilter, searchQuery } = this.state;
    const filteredItems = this.getFilteredItems();
    const dropdownItems = this.getDropdownItems();

    return (
      <div className="campaign-influencer-selector__content">
        <div className="campaign-influencer-selector__filters">
          {
            filterOptions && filterOptions.length > 0 &&
            <div className="campaign-influencer-selector__filter">
              <MixedSelectDropdown
                key={`campaign-influencer-selector__dropdown--${currentFilter}`}
                name="campaign-influencer-selector__dropdown"
                items={dropdownItems}
                withSelectedInList
                className="campaign-influencer-selector__dropdown"
                onItemsToggle={this.handleTasksChanged}
              />
            </div>
          }
          <InfluencerSelectorSearch
            searchQuery={searchQuery}
            onSearch={this.handleInfluencersSearch}
          />
        </div>
        <div className="campaign-influencer-selector__results">
          <InfluencerSelector
            influencerProfiles={filteredItems.map(s => s.creatorProfile)}
            selectedInfluencerProfileId={selectedItem ? selectedItem.creatorProfile.id : null}
            shouldSort={false}
            resetButton={resetButton}
            onSelect={this.handleInfluencerSelected}
          />
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { isLoading, disabled, title } = this.props;

    const containerClasses = classnames({
      'campaign-influencer-selector__container': true,
      'campaign-influencer-selector__container--disabled': disabled && !isLoading
    });

    return (
      <PageSection
        title={title}
        titleProps={{
          size: 'h1',
          margin: 'none',
          customClass: 'campaign-influencer-selector__heading'
        }}
        padding="none"
        margin="none"
        customClass="campaign-influencer-selector"
      >
        <section
          className={containerClasses}
          data-qa="campaign-influencer-selector"
        >
          {
            isLoading ?
              <Spinner
                position="inline"
                size="small"
                color="light"
              /> :
              this.renderContent()
          }
        </section>
      </PageSection>
    );
  }
}

export default CampaignInfluencerSelector;
